import { useEffect, useState } from 'react'
import { useTheme } from '@/context/ThemeContext'
import Footer from './Footer'
import './Career.css'

function Career() {
    const { resolved } = useTheme()
    const [scrollY, setScrollY] = useState(0)
    const [inView, setInView] = useState<Record<string, boolean>>({})

    useEffect(() => {
        // Reset scroll position when page loads
        window.scrollTo(0, 0)

        const handleScroll = () => {
            const y = window.scrollY
            setScrollY(y)
        }
        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    // Intersection observer for reveal animations
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const id = (entry.target as HTMLElement).dataset.reveal
                        if (id) setInView((prev) => ({ ...prev, [id]: true }))
                    }
                })
            },
            { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
        )

        // Query all reveal elements after mount
        const els = document.querySelectorAll('[data-reveal]')
        els.forEach((el) => observer.observe(el))
        return () => observer.disconnect()
    }, [])

    const heroScale = 1 + Math.min((scrollY / window.innerHeight) * 0.15, 0.15)
    const heroOpacity = Math.max(0, 1 - (scrollY / window.innerHeight) * 1.5)

    const vis = (id: string) => (inView[id] ? 'revealed' : '')

    return (
        <div className={`career-page ${resolved}`}>
            {/* ===== HERO ===== */}
            <section className="career-hero">
                <div className="career-media-layer">
                    <video autoPlay muted loop playsInline>
                        <source src="/Morning_flow_storyboard_sequence_fef8721a10.mp4" type="video/mp4" />
                    </video>
                </div>
                <div className="career-hero-gradient" />
                <div
                    className="career-hero-content"
                    style={{ transform: `scale(${heroScale})`, opacity: heroOpacity }}
                >
                    <span className="career-hero-tag">Join Our Team</span>
                    <h1 className="career-hero-heading">BUILD THE<br />FUTURE WITH US</h1>
                    <p className="career-hero-sub">Shape the next generation of AI solutions</p>
                </div>
            </section>

            {/* ===== WHY JOIN ===== */}
            <section className="career-section career-why">
                <div className="career-why-inner">
                    <div className={`career-reveal ${vis('why-text')}`} data-reveal="why-text">
                        <span className="career-section-tag">Why Antz AI</span>
                        <h2 className="career-section-heading">WHERE AMBITION<br />MEETS PURPOSE</h2>
                        <p className="career-section-text">
                            We're not just building technology — we're shaping how businesses think,
                            decide, and evolve. Join a team where your work directly transforms industries.
                        </p>
                    </div>
                    <div className={`career-why-grid career-reveal ${vis('why-grid')}`} data-reveal="why-grid">
                        {[
                            { title: 'Impact-Driven Work', desc: 'Every project solves real problems for real businesses. Your code ships and makes a difference.' },
                            { title: 'Growth Culture', desc: 'Continuous learning, mentorship, and room to explore new technologies and ideas.' },
                            { title: 'Office-First', desc: 'We build together in person — the best ideas come from people in the same room. Remote-friendly for the right roles, but we love when you\'re here.' },
                            { title: 'Innovation Focus', desc: 'Experiment with cutting-edge AI, data engineering, and automation technologies daily.' },
                        ].map((item, i) => (
                            <div key={i} className="career-glass-card" style={{ transitionDelay: `${i * 0.08}s` }}>
                                <h4 className="career-card-title">{item.title}</h4>
                                <p className="career-card-desc">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== CULTURE (Video Background) ===== */}
            <section className="career-culture-section">
                <div className="career-media-layer">
                    <video autoPlay muted loop playsInline>
                        <source src="/use-this-a-starting-image-storyboard-sequence-morn.mp4" type="video/mp4" />
                    </video>
                </div>
                <div className="career-hero-gradient" />
                <div className={`career-culture-content career-reveal ${vis('culture')}`} data-reveal="culture">
                    <span className="career-section-tag">Our Culture</span>
                    <h2 className="career-culture-heading">CURIOSITY.<br />COLLABORATION.<br />CRAFT.</h2>
                    <p className="career-culture-text">
                        We believe the best solutions emerge when diverse minds come together with a
                        shared obsession for quality. Our culture rewards bold thinking, honest
                        feedback, and relentless iteration.
                    </p>
                </div>
            </section>

            {/* ===== VALUES ===== */}
            <section className="career-values-wrapper">
                <div className={`career-reveal ${vis('values-header')}`} data-reveal="values-header">
                    <div className="career-values-header">
                        <span className="career-section-tag">Our Principles</span>
                        <h2 className="career-section-heading">WHAT WE<br />VALUE</h2>
                    </div>
                </div>

                <div className="career-value-blocks">
                    <div className={`career-value-card career-reveal ${vis('val-1')}`} data-reveal="val-1">
                        <h3 className="career-value-title">AUTONOMY & TRUST</h3>
                        <div className="career-value-traits">
                            <div className="career-trait">End-to-End Responsibility</div>
                            <div className="career-trait">Proactive Problem Solving</div>
                        </div>
                    </div>

                    <div className={`career-value-card career-reveal ${vis('val-2')}`} data-reveal="val-2" style={{ transitionDelay: '0.15s' }}>
                        <h3 className="career-value-title">EXCELLENCE</h3>
                        <p className="career-value-desc">Good enough isn't. We pursue mastery in everything — from code quality to client communication.</p>
                        <div className="career-value-traits-grid">
                            <div className="career-trait">High Standards</div>
                            <div className="career-trait">Continuous Improvement</div>
                            <div className="career-trait">Attention to Detail</div>
                            <div className="career-trait">Open Communication</div>
                            <div className="career-trait">Honest Feedback</div>
                            <div className="career-trait">Shared Context</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== JOB OPENINGS ===== */}
            <section className="career-openings-section">
                <div className="career-openings-inner">
                    <div className={`career-reveal ${vis('openings-header')}`} data-reveal="openings-header">
                        <span className="career-section-tag">Open Positions</span>
                        <h2 className="career-section-heading">JOB OPENINGS</h2>
                    </div>
                    <div className={`career-openings-list career-reveal ${vis('openings-list')}`} data-reveal="openings-list">
                        {[
                            {
                                category: 'Engineering',
                                openings: [
                                    { title: 'AI Engineer', location: 'Hyderabad', url: 'mailto:careers@antz.ai' },
                                ],
                            },
                            {
                                category: 'Growth',
                                openings: [
                                    { title: 'Product Growth Engineer', location: 'Remote', url: 'mailto:careers@antz.ai' },
                                ],
                            },
                        ].map((group, gi) => (
                            <div key={group.category} className="career-openings-group" style={{ transitionDelay: `${gi * 0.1}s` }}>
                                <h3 className="career-openings-category">{group.category}</h3>
                                {group.openings.map((job) => (
                                    <div key={job.title} className="career-openings-row">
                                        <div className="career-openings-info">
                                            <a href={job.url} className="career-openings-title">{job.title}</a>
                                            {job.title === 'Product Growth Engineer' && (
                                                <p className="career-openings-desc">Content strategy, social posts, video creation, product demos &amp; growth campaigns.</p>
                                            )}
                                            {job.title === 'AI Engineer' && (
                                                <p className="career-openings-desc">Design and build AI-powered systems, ML pipelines, and intelligent automation solutions.</p>
                                            )}
                                        </div>
                                        <span className="career-openings-location">{job.location}</span>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== CTA ===== */}
            <section className="career-cta-section">
                <div className="career-media-layer">
                    <video autoPlay muted loop playsInline>
                        <source src="/Morning_flow_storyboard_sequence_fef8721a10.mp4" type="video/mp4" />
                    </video>
                </div>
                <div className="career-hero-gradient" />
                <div className={`career-cta-content career-reveal ${vis('cta')}`} data-reveal="cta">
                    <h2 className="career-cta-heading">DON'T SEE<br />YOUR ROLE?</h2>
                    <p className="career-cta-text">
                        We're always looking for exceptional people. Send us your story and let's explore what's possible together.
                    </p>
                    <div className="career-cta-mailto-block">
                        <p className="career-cta-dont-see">Don't see a fit?</p>
                        <p className="career-cta-send-resume">Send us your resume at</p>
                        <a href="mailto:careers@antz.ai" className="career-cta-email-link">
                            careers@antz.ai
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    )
}

export { Career as CareersPage }
export default Career
